import * as Sentry from '@sentry/react'
import { ErrorInfo } from 'react'
import { FallbackProps } from 'react-error-boundary'
import { Alert, Stack, Code } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import {
  AppError,
  ApiError,
  AuthError,
  NetworkError,
  ValidationError,
  ErrorWithSentryId,
  formatZodError,
} from './errors'
import { ZodError } from 'zod'
import { Typography } from './typography'
import { Link } from 'react-router'

export function handleError(error: Error, info: ErrorInfo) {
  let errorToReport = error

  if (error instanceof ZodError) {
    errorToReport = formatZodError(error)
  }

  if (process.env.REACT_SENTRY_ENVIRONMENT !== 'local') {
    if (
      errorToReport instanceof AppError &&
      errorToReport.context.sentryReported
    ) {
      if (error && typeof error === 'object') {
        ;(error as ErrorWithSentryId).sentryEventId =
          errorToReport.sentryEventId
      }
    } else {
      const eventId =
        (errorToReport as AppError).reportToSentry?.() ||
        Sentry.captureException(errorToReport, {
          contexts: {
            react: {
              componentStack: info.componentStack,
            },
          },
        })

      if (error && typeof error === 'object') {
        ;(error as ErrorWithSentryId).sentryEventId = eventId
      }
    }
  }

  if (process.env.NODE_ENV === 'development') {
    console.error('[ErrorBoundary] Caught error:', error, info)
  }
}

export function ErrorFallback({ error }: FallbackProps) {
  let sentryEventId: string | undefined
  let displayError = error

  if (error instanceof ZodError) {
    displayError = formatZodError(error)
  }

  if (displayError instanceof AppError && displayError.sentryEventId) {
    sentryEventId = displayError.sentryEventId
  } else if (error && typeof error === 'object' && 'sentryEventId' in error) {
    sentryEventId = (error as ErrorWithSentryId).sentryEventId
  }

  let title = 'Something went wrong'
  let message = 'An unexpected error occurred. Please try again.'
  let showDetails = false

  if (displayError instanceof ApiError) {
    if (displayError.isClientError()) {
      title = 'Request Error'
      message = displayError.message
    } else if (displayError.isServerError()) {
      title = 'Server Error'
      message = 'Our servers encountered an issue. Please try again later.'
    }
    showDetails = true
  } else if (displayError instanceof AuthError) {
    title = 'Authentication Error'
    message = 'Your session has expired. Please log in again to continue.'
  } else if (displayError instanceof NetworkError) {
    title = 'Connection Error'
    message =
      'Unable to connect to the server. Please check your internet connection and try again.'
  } else if (displayError instanceof ValidationError) {
    title = 'Data Validation Error'
    message = "The data received doesn't match expected format."
    showDetails = true
  }

  return (
    <Stack p="xl" maw="600px" m="auto" mt="xl" gap="md">
      <Alert icon={<IconAlertCircle size="1rem" />} title={title} color="red">
        <Typography fc={{ light: 'black', dark: 'black' }}>
          {message}{' '}
          {sentryEventId && (
            <Link
              to={`mailto:support@leaguestar.com?subject=${encodeURIComponent(`Sentry ID: ${sentryEventId}`)}`}
              style={{ textDecorationColor: 'black' }}
            >
              <Typography fc={{ light: 'black', dark: 'black' }}>
                Contact support
              </Typography>
            </Link>
          )}
        </Typography>
      </Alert>
      {showDetails && process.env.NODE_ENV === 'development' && (
        <Stack gap="xs">
          <Typography fc={{ light: 'black', dark: 'black' }}>
            Technical Details:
          </Typography>
          <Code block>{displayError.message}</Code>
          {displayError instanceof AppError && (
            <Code block>{JSON.stringify(displayError.context, null, 2)}</Code>
          )}
        </Stack>
      )}
    </Stack>
  )
}
